import {
  ValidateNpiReasonCode,
  ValidationStatus,
} from "../__generated__/graphql";

import { PROVIDER_SUPPORT_PHONE_NUMBER } from "../../constants";

/** One practice address NPPES lists for the NPI. */
export type PracticeLocation = {
  address1: string;
  address2: string | null;
  city: string;
  state: string;
  zip: string;
  phone: string | null;
  fax: string | null;
};

/** What the sign-up NPI lookup found, to prefill step 1. */
export type NpiLookup = {
  npi: string;
  first_name: string;
  last_name: string;
  practice_locations: PracticeLocation[];
};

const SUPPORT = `Need help? Call us at ${PROVIDER_SUPPORT_PHONE_NUMBER}.`;

// Keyed by the backend's reason_code; any other code gets UNKNOWN_MESSAGE.
const REASON_MESSAGES: Partial<Record<ValidateNpiReasonCode, string>> = {
  [ValidateNpiReasonCode.MalformedNpi]:
    "This NPI number is not formatted correctly. Please try again.",
  [ValidateNpiReasonCode.NotFound]:
    "We couldn't find this NPI. Please check it and try again.",
  [ValidateNpiReasonCode.ProviderType]:
    "The number you entered belongs to an HHA, clinic, or group practice. Please use an individual clinician's NPI.",
  [ValidateNpiReasonCode.InactiveNpi]:
    "This NPI number is no longer active. Please try a different one.",
  [ValidateNpiReasonCode.ScopeOfPractice]:
    "This NPI isn't authorized to order DME per Medicare guidelines. You can use your supervising provider's NPI instead.",
};
const UNKNOWN_MESSAGE = "We couldn't verify this NPI.";

const UNAVAILABLE_MESSAGE = `We couldn't check your NPI right now. Please try again in a few minutes. ${SUPPORT}`;

/** Looks the NPI up for sign-up and checks the email with the submit's rule.
 * Resolves to what was found, the error to show under the email field, or the
 * error to show under the NPI field. */
export const lookupNpi = async (
  npi: string,
  email: string,
): Promise<
  { lookup: NpiLookup } | { emailError: string } | { error: string }
> => {
  try {
    const response = await fetch("/graphql/provider-signup/npi-lookup", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ npi, email }),
    });
    const body = await response.json();
    if (body.ok) {
      return { lookup: body.data };
    }
    // The only field error the lookup returns is on the email.
    if (Array.isArray(body.errors) && body.errors.length > 0) {
      return { emailError: String(body.errors[0].message) };
    }
    if (body.status === ValidationStatus.ServiceUnavailable) {
      return { error: UNAVAILABLE_MESSAGE };
    }
    const message =
      REASON_MESSAGES[body.reason_code as ValidateNpiReasonCode] ??
      UNKNOWN_MESSAGE;
    return { error: `${message} ${SUPPORT}` };
  } catch {
    return { error: UNAVAILABLE_MESSAGE };
  }
};
